import React from 'react';
import styled from 'styled-components';
import { config } from '../../config';

const InfoContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px 40px;
  
  @media (max-width: 768px) {
    padding: 60px 20px 40px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  color: #333;
  margin-bottom: 40px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Section = styled.section`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: #333;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const InfoCard = styled.div`
  background: white;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 18px;
  color: #333;
  font-weight: 600;
  word-break: break-word;
`;

const Link = styled.a`
  color: #667eea;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #555;
  margin-bottom: 16px;
`;

const List = styled.ul`
  margin: 16px 0 16px 24px;
  
  li {
    font-size: 16px;
    line-height: 1.8;
    color: #555;
    margin-bottom: 8px;
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  padding: 32px;
  margin: 32px 0;
  text-align: center;
`;

const CompanyInfo = () => {
  return (
    <InfoContainer>
      <Title>Company Information</Title>

      <Section>
        <SectionTitle>Contact Details</SectionTitle>
        <InfoGrid>
          <InfoCard>
            <InfoLabel>Company Name</InfoLabel>
            <InfoValue>{config.company.name}</InfoValue>
          </InfoCard>
          
          <InfoCard>
            <InfoLabel>Legal Name</InfoLabel>
            <InfoValue>{config.company.legalName}</InfoValue>
          </InfoCard>
          
          <InfoCard>
            <InfoLabel>Email</InfoLabel>
            <InfoValue>
              <Link href={`mailto:${config.company.email}`}>
                {config.company.email}
              </Link>
            </InfoValue>
          </InfoCard>
          
          <InfoCard>
            <InfoLabel>Phone</InfoLabel>
            <InfoValue>
              <Link href={`tel:${config.company.phone}`}>
                {config.company.phone}
              </Link>
            </InfoValue>
          </InfoCard>
          
          <InfoCard>
            <InfoLabel>Website</InfoLabel>
            <InfoValue>
              <Link href={config.company.website} target="_blank" rel="noopener noreferrer">
                {config.company.website}
              </Link>
            </InfoValue>
          </InfoCard>
        </InfoGrid>
      </Section>

      <Section>
        <SectionTitle>Registered Address</SectionTitle>
        <InfoCard>
          <InfoValue>{config.company.address}</InfoValue>
        </InfoCard>
      </Section>

      <Section>
        <SectionTitle>About Us</SectionTitle>
        <Paragraph>
          {config.company.name} is a leading provider of career development programs, educational 
          courses, and professional training services. We are committed to empowering individuals 
          with the skills and knowledge needed to succeed in today's competitive job market.
        </Paragraph>
        <Paragraph>
          Our comprehensive programs are designed by industry experts and include:
        </Paragraph>
        <List>
          <li>AI-Enhanced Career Programs</li>
          <li>Job-Ready Training Courses</li>
          <li>Industry-Specific Skill Development</li>
          <li>Career Counseling and Mentorship</li>
          <li>Placement Assistance Services</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>Business Hours</SectionTitle>
        <InfoCard>
          <InfoValue>
            Monday to Saturday: 10:00 AM - 6:00 PM IST<br />
            Sunday: Closed
          </InfoValue>
        </InfoCard>
      </Section>

      <Section>
        <SectionTitle>Payment Information</SectionTitle>
        <Paragraph>
          We accept payments through PhonePe, India's leading digital payment platform. 
          All transactions are processed securely with industry-standard encryption.
        </Paragraph>
        <InfoGrid>
          <InfoCard>
            <InfoLabel>Accepted Currency</InfoLabel>
            <InfoValue>Indian Rupees (INR) - ₹</InfoValue>
          </InfoCard>
          
          <InfoCard>
            <InfoLabel>Payment Methods</InfoLabel>
            <InfoValue>
              UPI, Credit Card, Debit Card, Net Banking, Wallets
            </InfoValue>
          </InfoCard>
          
          <InfoCard>
            <InfoLabel>Payment Gateway</InfoLabel>
            <InfoValue>PhonePe Payment Gateway</InfoValue>
          </InfoCard>
          
          <InfoCard>
            <InfoLabel>Refund Policy</InfoLabel>
            <InfoValue>
              <Link href="/refund-policy">View Refund Policy</Link>
            </InfoValue>
          </InfoCard>
        </InfoGrid>
      </Section>

      <HighlightBox>
        <h2 style={{ marginBottom: '16px', fontSize: '24px' }}>Need Help?</h2>
        <Paragraph style={{ color: 'white', marginBottom: '24px' }}>
          Our support team is available to assist you with any questions or concerns.
        </Paragraph>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link 
            href={`mailto:${config.company.email}`}
            style={{ 
              background: 'white', 
              color: '#667eea', 
              padding: '12px 24px', 
              borderRadius: '8px',
              fontWeight: '600'
            }}
          >
            Email Us
          </Link>
          <Link 
            href={`tel:${config.company.phone}`}
            style={{ 
              background: 'white', 
              color: '#667eea', 
              padding: '12px 24px', 
              borderRadius: '8px',
              fontWeight: '600'
            }}
          >
            Call Us
          </Link>
        </div>
      </HighlightBox>

      <Section>
        <SectionTitle>Legal & Policies</SectionTitle>
        <InfoGrid>
          <InfoCard>
            <InfoLabel>Privacy Policy</InfoLabel>
            <InfoValue>
              <Link href="/privacy-policy">Read Privacy Policy</Link>
            </InfoValue>
          </InfoCard>
          
          <InfoCard>
            <InfoLabel>Terms & Conditions</InfoLabel>
            <InfoValue>
              <Link href="/terms-and-conditions">Read Terms & Conditions</Link>
            </InfoValue>
          </InfoCard>
          
          <InfoCard>
            <InfoLabel>Refund Policy</InfoLabel>
            <InfoValue>
              <Link href="/refund-policy">Read Refund Policy</Link>
            </InfoValue>
          </InfoCard>
          
          <InfoCard>
            <InfoLabel>Shipping Policy</InfoLabel>
            <InfoValue>
              <Link href="/shipping-policy">Read Shipping Policy</Link>
            </InfoValue>
          </InfoCard>
        </InfoGrid>
      </Section>
    </InfoContainer>
  );
};

export default CompanyInfo;
